"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn, pubsub } from "utils";
import { useEffect, useState } from "react";
import log from "logbeacon";
import { useShellRouter } from "@/src/hooks";
import { HeaderData, HeaderType } from "../../utils/headerTypes";

import { processDataByKey } from "../../utils/headerDataProcessor";
import { pubSubKeyMap } from "../../utils/pubSubKeyMap";
import BitcoinPriceDisplay from "./BitcoinPriceDisplay";
import { I18nUtil } from "@utils/i18n";
import { runShellAwarePageBack } from "@/src/lib/bridge/shellAppNavigation";
import { ROUTER_PATH } from "@/src/config";
import Translations from "@translate/components/mainHeader/Back";
import { getBridge } from "@/src/lib/bridge";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 根据 HeaderData 的 key 渲染对应的组件
 */
const renderHeaderComponent = (data: HeaderData | null) => {
  if (!data?.key) {
    return null;
  }

  // 使用统一的数据处理逻辑
  const formattedData = processDataByKey(data);

  switch (data.key) {
    case HeaderType.PRICE_DISPLAY:
    case HeaderType.STOCK_INFO:
      return (
        <BitcoinPriceDisplay
          formattedData={formattedData}
          tokenData={data.tokenData || {}}
          isPollToken={data.isPollToken}
        />
      );

    case HeaderType.MARKET_INFO:
      // 预留：未来可以添加市场信息组件
      return (
        <div className="text-content_primary body_medium_s">
          {data.title || "Market Info"}
        </div>
      );

    case HeaderType.CUSTOM:
      // 预留：自定义组件
      return (
        <div className="text-content_primary body_medium_s">
          {data.title || "Custom"}
        </div>
      );

    default:
      return null;
  }
};

export const Back = ({
  title,
  goBack,
  isGoHome = false,
  iconSize = 24,
  className,
  titleClassName,
  iconClassName,
}: {
  title?: string;
  goBack?: () => void;
  isGoHome?: boolean;
  iconSize?: number;
  className?: string;
  titleClassName?: string;
  iconClassName?: string;
}) => {
  const router = useShellRouter();
  const [headerData, setHeaderData] = useState<HeaderData | null>(null);

  const runBack = () => {
    if (typeof goBack === "function") {
      goBack();
      return;
    }
    handleGoBackDefault();
  };

  const handleGoBackDefault = () => {
    log.debug("[文案]handleGoBack");
    if (getBridge().isInApp()) {
      runShellAwarePageBack(router, { isGoHome });
      return;
    }
    const mainPath = sessionStorage.getItem("mainPath");
    const currHref = window.location.href;
    const isBack = mainPath !== currHref && !isGoHome;
    log.debug("[文案]isBack", isBack, mainPath, currHref, isGoHome);
    if (isBack) {
      log.debug("[文案]router.back()");
      router.back();
    } else {
      log.debug("[文案]router.push(ROUTER_PATH.HOME)");
      router.push(ROUTER_PATH.HOME);
    }
  };

  // 订阅 header 数据
  useEffect(() => {
    const unsubscribe = pubsub.on(
      pubSubKeyMap.PRICE_HEADER_DATA,
      (data: HeaderData) => {
        setHeaderData(data);
      },
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const shouldShowHeader = !!headerData?.key;

  return (
    <div
      data-openreplay-label="mainHeader · Back · div"
      className={cn("flex items-center cursor-pointer", className)}
      onClick={runBack}
    >
      {/* V3.0.3: H5 back button 全局样式 — 白色圆形(40px) + drop-shadow (Figma 全站标准) */}
      <div
        className={cn(
          "md:bg-transparent md:shadow-none md:w-auto md:h-auto md:rounded-none",
          "flex items-center justify-center w-10 h-10 rounded-full bg-background_inverse_overlay100 drop-shadow-[0px_2px_10px_rgba(0,0,0,0.08)]",
        )}
      >
        <Icon
          className={cn(iconClassName)}
          name="left-arrow-alt"
          size={iconSize}
          color={ospThemes.colors.content_primary}
        />
      </div>
      <AnimatePresence mode="wait">
        {!shouldShowHeader ? (
          <motion.span
            key="back-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={cn(
              "hidden md:block text-content_primary body_medium_l max-w-[560px] truncate",
              titleClassName,
            )}
          >
            {title || I18n.t("Back")}
          </motion.span>
        ) : (
          <motion.div
            key="header-component"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {renderHeaderComponent(headerData)}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
